import { Head, Link, useForm } from "@inertiajs/react"
import { InfoCircle } from "@untitledui/icons"
import { useMemo } from "react"

import { PageHeader } from "@/components/console/page-header"
import { Button } from "@/components/ui/button"
import { InputBase, Label, TextField } from "@/components/ui/input"
import { NativeSelect } from "@/components/ui/select"
import { AdminLayout } from "@/layouts/admin-layout"
import { toFieldError } from "@/lib/utils"
import { adminMsgFlowRunsPath } from "@/routes"

interface Definition {
  code: string
  version: number
  label: string
  template_locals_schema: string[]
}

interface Props {
  definitions: Definition[]
  default_account_name: string
  system_phone_number: string
}

interface FormData {
  definition_code: string
  first_name: string
  last_name: string
  email: string
  phone_number: string
  start_at: string
  template_locals: Record<string, string>
  [key: string]: string | Record<string, string>
}

const buildInitialData = (definitions: Definition[]): FormData => {
  const first = definitions[0]
  return {
    definition_code: first?.code ?? "",
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    start_at: "",
    template_locals: localsTemplateFor(first),
  }
}

const localsTemplateFor = (
  definition: Definition | undefined,
): Record<string, string> => {
  if (!definition) return {}
  return Object.fromEntries(
    definition.template_locals_schema.map((k) => [k, ""]),
  )
}

const humanizeKey = (key: string) =>
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())

export default function AdminMsgFlowRunsNew({
  definitions,
  default_account_name,
  system_phone_number,
}: Props) {
  const { data, setData, post, processing, errors } = useForm<FormData>(
    buildInitialData(definitions),
  )

  const selectedDefinition = useMemo(
    () => definitions.find((d) => d.code === data.definition_code),
    [definitions, data.definition_code],
  )

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    post(adminMsgFlowRunsPath())
  }

  const onDefinitionChange = (code: string) => {
    const next = definitions.find((d) => d.code === code)
    setData((prev) => ({
      ...prev,
      definition_code: code,
      template_locals: {
        ...localsTemplateFor(next),
        // Carry forward shared keys (e.g. first_name) where they overlap.
        ...Object.fromEntries(
          Object.entries(prev.template_locals).filter(([k]) =>
            next?.template_locals_schema.includes(k),
          ),
        ),
      },
    }))
  }

  const onLocalChange = (key: string, value: string) => {
    setData((prev) => ({
      ...prev,
      template_locals: { ...prev.template_locals, [key]: value },
    }))
  }

  // When the contact's first_name changes, mirror it into template_locals if
  // the chosen Definition asks for first_name (DemoSeeder does this explicitly
  // in app/domains/msg_flow/demo_seeder.rb).
  const onFirstNameChange = (value: string) => {
    setData((prev) => ({
      ...prev,
      first_name: value,
      template_locals: Object.prototype.hasOwnProperty.call(
        prev.template_locals,
        "first_name",
      )
        ? { ...prev.template_locals, first_name: value }
        : prev.template_locals,
    }))
  }

  const baseError = (errors as Record<string, string | undefined>).base

  return (
    <AdminLayout>
      <Head title="Start a MsgFlow run" />
      <div className="mx-auto flex max-w-3xl flex-col gap-6 px-4 lg:px-8">
        <PageHeader
          crumbs={[
            { label: "message flows" },
            { label: "runs", href: adminMsgFlowRunsPath() },
            { label: "new" },
          ]}
          title="Start a MsgFlow run"
          description={
            <>
              Enrols a contact into a Definition and dispatches the first SMS
              via Telnyx ({system_phone_number}). New contacts land under the
              &quot;{default_account_name}&quot; account.
            </>
          }
        />

        <div className="remote text-secondary flex items-start gap-3 p-4 text-sm">
          <InfoCircle className="mt-0.5 size-5 shrink-0" />
          <div>
            The first outbound SMS is scheduled as a Tick — it dispatches when
            the ActiveJob worker runs it. Quiet hours (Perth 21:00–08:00) shift
            the send to the next available window.
          </div>
        </div>

        {baseError && (
          <div className="caution-band text-caution-700 px-4 py-3 text-sm">
            {baseError}
          </div>
        )}

        <form onSubmit={submit} className="flex flex-col gap-6">
          <FormSection title="Definition">
            <NativeSelect
              label="Flow"
              value={data.definition_code}
              onChange={(e) => onDefinitionChange(e.target.value)}
              required
              options={definitions.map((d) => ({
                label: `${d.label} (v${d.version})`,
                value: d.code,
              }))}
            />
          </FormSection>

          <FormSection title="Contact">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <TextField
                isRequired
                name="first_name"
                value={data.first_name}
                onChange={onFirstNameChange}
                error={toFieldError(errors.first_name)}
              >
                <Label>First name</Label>
                <InputBase size="md" />
              </TextField>

              <TextField
                isRequired
                name="last_name"
                value={data.last_name}
                onChange={(v) => setData("last_name", v)}
                error={toFieldError(errors.last_name)}
              >
                <Label>Last name</Label>
                <InputBase size="md" />
              </TextField>

              <TextField
                name="email"
                type="email"
                value={data.email}
                onChange={(v) => setData("email", v)}
                error={toFieldError(errors.email)}
              >
                <Label>Email (optional)</Label>
                <InputBase
                  size="md"
                  placeholder="your ...@batyr.com.au email"
                />
              </TextField>

              <TextField
                isRequired
                name="phone_number"
                value={data.phone_number}
                onChange={(v) => setData("phone_number", v)}
                error={toFieldError(errors.phone_number)}
              >
                <Label>Phone number</Label>
                <InputBase
                  size="md"
                  placeholder="+61412345678 or 0412 345 678"
                />
              </TextField>
            </div>
          </FormSection>

          {selectedDefinition &&
            selectedDefinition.template_locals_schema.length > 0 && (
              <FormSection
                title="Template locals"
                hint={`Required for ${selectedDefinition.label}.`}
              >
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {selectedDefinition.template_locals_schema.map((key) => (
                    <TextField
                      key={key}
                      isRequired
                      name={`template_locals[${key}]`}
                      value={data.template_locals[key] ?? ""}
                      onChange={(v) => onLocalChange(key, v)}
                      error={toFieldError(
                        (errors as Record<string, string | undefined>)[
                          `template_locals.${key}`
                        ],
                      )}
                    >
                      <Label>{humanizeKey(key)}</Label>
                      <InputBase size="md" />
                    </TextField>
                  ))}
                </div>
              </FormSection>
            )}

          <FormSection title="Schedule" hint="Defaults to now.">
            <TextField
              name="start_at"
              type="datetime-local"
              value={data.start_at}
              onChange={(v) => setData("start_at", v)}
              error={toFieldError(errors.start_at)}
            >
              <Label>Start at</Label>
              <InputBase size="md" />
            </TextField>
          </FormSection>

          <div className="flex justify-end gap-3">
            <Link href={adminMsgFlowRunsPath()}>
              <Button color="secondary" size="md">
                Cancel
              </Button>
            </Link>
            <Button
              type="submit"
              color="primary"
              size="md"
              isDisabled={processing}
            >
              Start run
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  )
}

function FormSection({
  title,
  hint,
  children,
}: {
  title: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <section className="border-secondary bg-primary flex flex-col gap-4 rounded-xl border p-5">
      <div className="flex flex-col gap-1">
        <h2 className="text-primary text-md font-semibold">{title}</h2>
        {hint && <p className="text-tertiary text-sm">{hint}</p>}
      </div>
      {children}
    </section>
  )
}
